/* File: frontend/src/components/reputation/BattleAnalysisView.js */
/* Description: Full-featured Battle Report & Attack Plan Command Center. */

import React, { useState, useEffect } from 'react';
import {
    X, ShieldAlert, AlertTriangle, FileText,
    BarChart3, Zap, ArrowRight, Scan, Search,
    Clock, Activity, Settings, Trophy, MapPin, Calendar,
    CheckCircle, Loader2, Code2
} from 'lucide-react';

// Helper function to format rank display
const formatRankDisplay = (rank) => {
    if (rank === null || rank === undefined) return "—";
    if (rank >= 999) return "Not in Top 10";
    return `#${rank}`;
};

const BattleAnalysisView = ({ battle, onClose, onCreateContent }) => {
    // [FIX] Respect initialTab prop
    const [activeTab, setActiveTab] = useState(battle.initialTab || 'report');

    if (!battle) return null;

    // [FIX] Query Fallback Logic
    let displayKeyword = battle.keyword;
    if (!displayKeyword && battle.title) {
        displayKeyword = battle.title.split(' (')[0];
    }
    if (displayKeyword && displayKeyword.startsWith("Auto: ")) {
        displayKeyword = displayKeyword.replace("Auto: ", "");
    }
    displayKeyword = displayKeyword || "Unknown Query";

    // --- MOCK DATA FOR GAP ANALYSIS (Simulating the "Brain") ---
    // In a real app, this would come from an API endpoint call like /analyze-gap
    const analysis = {
        schemaGap: "Critical",
        contentDepth: { us: 45, competitor: 85 },
        authorityScore: { us: 32, competitor: 78 },
        topCompetitor: battle.competitor || "Morgan Legal Group",
        detectedIssues: [
            "Missing 'LegalService' Schema markups",
            "Competitor has 3x more local citation density",
            "First-person narrative missing in key sections"
        ]
    };

    // Helper: Parse Competitors (Smarter Regex for "1. Name - Description")
    const getCompetitors = () => {
        if (battle.competitors && battle.competitors.length > 0) return battle.competitors;

        const text = battle.message_content || battle.initial_message || "";
        // Regex to capture "1. Name" before a hyphen, colon, or newline
        const regex = /(?:^|\n)\d+\.\s+(?:\*\*)?([^-:\n]+?)(?:\*\*)?(?:\s+[-–:]|\n|$)/g;

        const matches = [...text.matchAll(regex)].map(m => m[1].trim());
        const cleaned = matches.filter(name =>
            name.length > 3 &&
            !name.includes("Expertise") &&
            !name.includes("Conclusion")
        );
        return cleaned.length > 0 ? cleaned.slice(0, 5) : [];
    };
    const competitors = getCompetitors();

    // Helper: Parse Chat with better error handling & JSON support
    const getMessages = () => {
        // 1. Try explicit messages array
        if (battle.messages && battle.messages.length > 0) return battle.messages;

        // 2. Try parsing message_content if it's a JSON string
        try {
            if (battle.message_content && typeof battle.message_content === 'string') {
                if (battle.message_content.trim().startsWith('[')) {
                    return JSON.parse(battle.message_content);
                }
            }
        } catch (e) {
            console.error("Failed to parse message_content JSON", e);
        }

        // 3. Fallback: Create simulated chat from snapshot
        const snapshot = battle.message_content || battle.initial_message;
        const q = battle.keyword ? `Who are the best ${battle.keyword}?` : "Analyzer Query";
        return [
            { role: 'user', content: q },
            { role: 'assistant', content: snapshot || "No data available." }
        ];
    };
    const messages = getMessages();

    // Helper: Simple Markdown Formatter (Bold, List)
    const formatMessage = (text) => {
        if (!text) return "";
        return text.split('\n').map((line, i) => {
            // Bold: **text** -> <strong>text</strong>
            const parts = line.split(/(\*\*.*?\*\*)/g);
            return (
                <div key={i} style={{ minHeight: '1.2em', marginBottom: '4px' }}>
                    {parts.map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={j}>{part.slice(2, -2)}</strong>;
                        }
                        return part;
                    })}
                </div>
            );
        });
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
            <div className="battle-analysis-card" style={{
                width: '900px', height: '85vh', backgroundColor: '#fff', borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden',
                display: 'flex', flexDirection: 'column'
            }}>

                {/* 1. HEADER (Detailed) */}
                <div style={{
                    padding: '20px 30px', backgroundColor: '#fff',
                    borderBottom: '1px solid #e2e8f0', display: 'flex',
                    flexDirection: 'column', gap: '12px'
                }}>
                    {/* Top Row: Badge + Meta + Close */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{
                                background: '#fee2e2', color: '#dc2626',
                                padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800',
                                textTransform: 'uppercase', letterSpacing: '0.05em'
                            }}>
                                LOST BATTLE
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.9rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <MapPin size={14} />
                                    <span style={{
                                        fontWeight: '700', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em',
                                        color: '#94a3b8'
                                    }}>
                                        {battle.location || 'Local Market'}
                                    </span>
                                </div>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Calendar size={14} /> <span style={{ fontWeight: '500' }}>{new Date(battle.created_at || Date.now()).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>
                        <button onClick={onClose} style={{
                            background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer'
                        }}><X size={24} /></button>
                    </div>

                    {/* Bottom Row: Keyword */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>KEYWORD:</span>
                        <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b', margin: 0 }}>
                            "{displayKeyword}"
                        </h2>
                    </div>
                </div>

                {/* 2. TABS */}
                <div style={{
                    padding: '0 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '30px'
                }}>
                    {['report', 'attack', 'audit', 'settings'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '16px 0', background: 'transparent', border: 'none',
                                borderBottom: activeTab === tab ? '3px solid #2563eb' : '3px solid transparent',
                                color: activeTab === tab ? '#2563eb' : '#64748b',
                                fontWeight: activeTab === tab ? '700' : '500',
                                fontSize: '0.95rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'capitalize'
                            }}
                        >
                            {tab === 'report' && <Clock size={16} />}
                            {tab === 'attack' && <Zap size={16} />}
                            {tab === 'audit' && <Activity size={16} />}
                            {tab === 'settings' && <Settings size={16} />}
                            {tab === 'report' ? 'Battle Report' : tab === 'attack' ? 'Attack Plan' : tab}
                        </button>
                    ))}
                </div>

                {/* 3. CONTENT AREA */}
                <div style={{ flex: 1, backgroundColor: '#f1f5f9', padding: '30px', overflowY: 'auto' }}>

                    {/* --- TAB: BATTLE REPORT --- */}
                    {activeTab === 'report' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                            {/* CARD 1: TARGET ANALYSIS */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e293b', marginBottom: '20px' }}>Target Analysis</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px' }}>

                                    {/* Left: Our Rank */}
                                    <div>
                                        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>OUR RANK</div>
                                        <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#fda4af' }}>
                                            {formatRankDisplay(battle.rank)}
                                        </div>
                                    </div>

                                    {/* Right: Competitors */}
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' }}>COMPETITORS</div>
                                            <div style={{
                                                fontSize: '0.7rem', color: '#10b981', background: '#d1fae5',
                                                padding: '2px 8px', borderRadius: '4px', fontWeight: '600'
                                            }}>
                                                AUTOMATIC PROCESS ACTIVE
                                            </div>
                                        </div>
                                        {competitors.length > 0 ? (
                                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                                {competitors.map((c, i) => <li key={i}>{c}</li>)}
                                            </ul>
                                        ) : (
                                            <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.9rem' }}>No competitor list extracted.</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* CARD 2: BATTLE LOG (ARENA STYLE) */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e293b', marginBottom: '20px' }}>Battle Log</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {messages.map((msg, i) => {
                                        const isUser = msg.role === 'user' || msg.role === 'human' || msg.role === 'director';
                                        return (
                                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>

                                                {/* Meta Label */}
                                                <div style={{
                                                    marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
                                                    fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em'
                                                }}>
                                                    {isUser ? (
                                                        <>DIRECTOR (YOU) <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }}></div></>
                                                    ) : (
                                                        <><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#64748b' }}></div> THE SQUAD</>
                                                    )}
                                                </div>

                                                {/* Chat Bubble */}
                                                <div style={{
                                                    width: '100%',
                                                    borderRadius: '8px',
                                                    padding: '16px',
                                                    backgroundColor: isUser ? '#eff6ff' : 'white', // Blue-50 : White
                                                    border: isUser ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                                    color: '#334155',
                                                    fontSize: '0.85rem',
                                                    lineHeight: '1.5'
                                                }}>
                                                    {formatMessage(msg.content)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ACTION FOOTER */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                                <button
                                    onClick={() => setActiveTab('attack')}
                                    style={{
                                        padding: '12px 24px', backgroundColor: '#2563eb', color: 'white',
                                        border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '0.95rem',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                                        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                                    }}
                                >
                                    Proceed to Attack Plan <ArrowRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* --- TAB: ATTACK PLAN --- */}
                    {activeTab === 'attack' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#1e293b', marginBottom: '20px' }}>Recommended Strategy</h3>

                                <div style={{
                                    border: '2px solid #8b5cf6', borderRadius: '12px', padding: '24px',
                                    backgroundColor: '#f5f3ff', position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'absolute', top: -12, right: 24,
                                        backgroundColor: '#8b5cf6', color: 'white', fontWeight: '700',
                                        fontSize: '0.75rem', padding: '4px 12px', borderRadius: '20px'
                                    }}>
                                        AI RECOMMENDATION
                                    </div>

                                    <div style={{ display: 'flex', gap: '20px' }}>
                                        <div style={{
                                            width: '50px', height: '50px', backgroundColor: 'white', borderRadius: '10px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                        }}>
                                            <Zap size={24} className="text-violet-600" />
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>Content Warfare Campaign</h4>
                                            <p style={{ color: '#4b5563', lineHeight: '1.5', marginBottom: '20px' }}>
                                                Based on the analysis, we need to deploy a <strong>Cornerstone Article</strong> targeting "{battle.keyword}" in {battle.location}.
                                                This will include LocalBusiness Schema and specific location entities to outrank {competitors[0] || 'competitors'}.
                                            </p>
                                            <button
                                                onClick={() => onCreateContent(`DETECTED GAP: Critical ranking loss for '${battle.keyword}'. EXECUTE STRATEGY: Create authoritative guide for ${battle.location} with Schema to outrank ${competitors[0] || 'competitors'}.`)}
                                                style={{
                                                    padding: '10px 20px', backgroundColor: '#8b5cf6', color: 'white',
                                                    border: 'none', borderRadius: '8px', fontWeight: '700',
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
                                                }}
                                            >
                                                <Zap size={16} fill="white" /> Launch Content Factory
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- TAB: AUDIT --- */}
                    {activeTab === 'audit' && (
                        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                            <Activity size={48} style={{ opacity: 0.5, marginBottom: '20px' }} />
                            <h3>Technical Audit</h3>
                            <p>Detailed technical metrics and schema validation.</p>
                            <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', textAlign: 'left' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span>Schema.org Validation</span>
                                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>Failed (0/3)</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span>Local Citations</span>
                                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>Weak (12 found)</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Content Depth</span>
                                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>Thin (450 words)</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- TAB: SETTINGS --- */}
                    {activeTab === 'settings' && (
                        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                            <Settings size={48} style={{ opacity: 0.5, marginBottom: '20px' }} />
                            <h3>Battle Settings</h3>
                            <p>Configure frequency and agents for this specific keyword.</p>
                        </div>
                    )}

                </div>

            </div>
        </div>
    );
};

export default BattleAnalysisView;
